// Website orders — hub → Website orders (CEO 2026-10-06).
//
// Paid website checkouts land here, not in the team's projects/orders: the
// database is shared and nobody sees the rebrand before launch. Each order
// shows what the customer bought, what they paid (items, shipping, tax),
// where it ships and their logo with its measured colours. Turn it into a
// workflow order by hand, then mark it converted here.
import { useCallback, useEffect, useRef, useState } from "react";
import { ShoppingBag, Loader2, Download, Check, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { nearestPms } from "@/lib/nearest-pms";
import { cn } from "@/lib/utils";
import { RoutingQueue, WebsiteAccountPanel, useFlowSettings } from "./WebsiteOrderRouting";
import { LocationPicker, type CompanyLocations, type LocationChoice } from "@/components/website/LocationPicker";

type Status = "paid" | "open" | "expired" | "converted" | "cancelled";
interface Address { street1: string; street2?: string | null; city: string; stateCode: string; postalCode: string }
interface Contact { name: string; email: string; phone?: string | null; company?: string | null; buyerType?: "company" | "individual" | null; department?: string | null; billing?: (Address & { name?: string | null; country?: string | null }) | null }
interface ShipTo extends Address { residential: boolean; recipient?: string | null }
interface Row { id: string; status: Status; contact: Contact; ship_to: ShipTo; subtotal: string; shipping: string; tax: string | null; total_paid: string | null; created_at: string; paid_at: string | null; item_count: number; reference?: string; artwork_missing?: number; rep_name?: string | null; rep_source?: "account" | "picked" | "none" }
interface PricedItem { name: string; quantity: number; colours: string[]; total: number; lines: Array<{ label: string; amount: string }>; print: string | null; logosNeedReview: boolean }
interface InputItem { productId: string; colorQuantities: Array<{ color: string; quantities: Record<string, number> }>; decoration: { method: string; locations: string[] } | null; logoIds: string[] }
interface Detail extends Row {
  /** Present once the Rep column exists (scripts/push-website-order-rep.ts). */
  assigned_rep_id?: string | null;
  /** The account this customer's email already belongs to, and its rep. */
  existing_account?: Account | null;
  /** The account the email matches, even when staff chose a new one instead. */
  matched_account?: Account | null;
  /** Staff chose "not this account — make a new one". */
  new_account?: boolean | null;
  /** The name they typed for it. */
  new_account_name?: string | null;
  /** Someone new at a company already in Flow (Bozzuto): its locations to pick from. */
  location_choices?: CompanyLocations[];
  /** The location staff picked (scripts/push-website-order-location.ts). */
  picked_client_id?: string | null;
  items: InputItem[];
  priced: { items: PricedItem[]; transitDays: number | null };
  notes: string | null;
  artwork_missing_items?: number[];
  logos: Array<{ id: string; name: string; url: string | null; analysis: { inkColours?: number | null; fullColour?: boolean; needsReview?: boolean; palette?: string[] } | null }>;
}

const money = (v: string | number | null | undefined) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }));
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const STATUS_WORD: Record<Status, [string, string]> = {
  paid: ["To do", "bg-warn-bg text-warn"],
  converted: ["Converted", "bg-ok-bg text-ok"],
  cancelled: ["Cancelled", "bg-canvas text-muted"],
  open: ["Not paid", "bg-canvas text-muted"],
  expired: ["Abandoned", "bg-canvas text-muted"],
};
const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink";

/** A modal side panel: focus moves in, Tab stays in, Escape closes, focus
 *  returns to whatever opened it, and the page behind does not scroll. */
function useModal(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = ref.current;
    const focusables = () => Array.from(panel?.querySelectorAll<HTMLElement>('button, a[href], input, [tabindex]:not([tabindex="-1"])') ?? []).filter((el) => !el.hasAttribute("disabled"));
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab") return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0]!, last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, [onClose]);
  return ref;
}

export function WebsiteOrdersPage() {
  const [filter, setFilter] = useState<"paid" | "all" | "requests" | "routing">("paid");
  const flow = useFlowSettings();
  // Deep links from the rep / manager emails: ?order=<id> or ?request=<id>.
  const [openRequestId, setOpenRequestId] = useState<string | null>(() => new URLSearchParams(window.location.search).get("request"));
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(() => new URLSearchParams(window.location.search).get("order"));
  const [newRequests, setNewRequests] = useState<number | null>(null);
  const countRequests = useCallback(() => {
    api<{ requests: unknown[] }>("/website-requests?status=new")
      .then((r) => setNewRequests(r.requests.length))
      .catch(() => setNewRequests(null));
  }, []);
  useEffect(() => { countRequests(); }, [countRequests]);

  const load = useCallback(async () => {
    setError(null);
    setRows(null);
    if (filter === "requests" || filter === "routing") return;
    try {
      const r = await api<{ orders: Row[] }>(`/website-orders?status=${filter}`);
      setRows(r.orders);
    } catch (e) { setError(apiErrorMessage(e, "Couldn't load website orders.")); }
  }, [filter]);
  useEffect(() => { void load(); }, [load]);
  const close = useCallback(() => setOpenId(null), []);

  return (
    <div className="mx-auto w-full px-6 py-12 md:px-10">
      <header className="flex flex-col gap-3 border-b border-rule pb-6">
        <div className="flex items-center gap-3">
          <ShoppingBag className="h-6 w-6" strokeWidth={1.5} aria-hidden />
          <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px]">Website orders</h1>
        </div>
        <p className="max-w-[62ch] text-[16px] text-muted">
          Orders customers have paid for on the website. Check the logo, create the order in the
          workflow, then mark it converted here. Requests are what customers sent without paying —
          items we can't price online and "tell us what you need" messages.
        </p>
        <div className="mt-2 flex gap-6" role="group" aria-label="Which orders">
          {([["paid", "To do"], ["all", "All orders"], ["requests", "Requests"], ...(flow?.on ? [["routing", "By rep"]] as const : [])] as const).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}
              className={cn("flex items-center gap-1.5 border-b-2 pb-2 text-[15px] font-semibold", focusRing, filter === k ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink")}>
              {label}
              {k === "requests" && newRequests ? (
                <span className="rounded-full bg-warn-bg px-1.5 text-[13px] font-bold text-warn" aria-label={`${newRequests} new`}>{newRequests}</span>
              ) : null}
            </button>
          ))}
        </div>
      </header>

      {filter === "requests" ? <RequestsList onChanged={countRequests} /> : null}
      {filter === "routing" ? <RoutingQueue onOpen={(kind, id) => (kind === "order" ? setOpenId(id) : setOpenRequestId(id))} /> : null}
      {filter !== "requests" && filter !== "routing" && error ? (
        <div role="alert" className="mt-6 flex items-center gap-4">
          <p className="text-[16px] text-risk">{error}</p>
          <Button variant="outline" onClick={() => void load()}>Try again</Button>
        </div>
      ) : null}
      {filter !== "requests" && filter !== "routing" && rows === null && !error ? <p className="mt-6 flex items-center gap-2 text-[16px] text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p> : null}
      {rows && rows.length === 0 ? (
        <p className="mt-6 text-[16px] text-muted">{filter === "paid" ? "Nothing waiting. New paid orders appear here." : "No website orders yet."}</p>
      ) : null}

      {rows && rows.length > 0 ? (
        <div className="mt-6 overflow-x-auto rounded-lg border border-rule">
          <table className="w-full min-w-[760px] text-left text-[13px] tabular-nums">
            <caption className="sr-only">Website orders: customer, destination, items, amount charged, date and status</caption>
            <thead className="bg-canvas text-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Customer</th>
                <th scope="col" className="px-4 py-3 font-semibold">Ships to</th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">Items</th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">Charged</th>
                <th scope="col" className="px-4 py-3 font-semibold">Rep</th>
                <th scope="col" className="px-4 py-3 font-semibold">When</th>
                <th scope="col" className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-rule">
                  <th scope="row" className="px-4 py-3 text-left font-normal">
                    <button type="button" className={cn("rounded text-left font-semibold underline-offset-2 hover:underline", focusRing)} onClick={() => setOpenId(r.id)}>
                      {r.contact.company || r.contact.name}
                    </button>
                    <div className="text-muted">{r.contact.email}</div>
                  </th>
                  <td className="px-4 py-3">{r.ship_to.city}, {r.ship_to.stateCode}</td>
                  <td className="px-4 py-3 text-right">{r.item_count}</td>
                  <td className="px-4 py-3 text-right font-semibold">{money(r.total_paid)}</td>
                  <td className={cn("px-4 py-3", !r.rep_name && "text-muted")}>
                    {r.rep_name ?? "—"}
                    {r.rep_source === "account" ? <div className="text-[12px] text-muted">Account rep</div> : null}
                  </td>
                  <td className="px-4 py-3">{when(r.paid_at ?? r.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={cn("inline-block whitespace-nowrap rounded px-1.5 font-semibold", STATUS_WORD[r.status][1])}>{STATUS_WORD[r.status][0]}</span>
                    {r.artwork_missing ? <ArtworkMissingBadge count={r.artwork_missing} /> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {openId ? <OrderDetail id={openId} onClose={close} onChanged={() => { setOpenId(null); void load(); }} /> : null}
      {openRequestId ? <RequestDetail id={openRequestId} onClose={() => setOpenRequestId(null)} onChanged={() => { setOpenRequestId(null); countRequests(); }} /> : null}
    </div>
  );
}

type RepBoxState = "on" | "needs_setup" | "flow";
interface Account { client_id: string; client_name: string; rep_id: string | null; rep_name: string | null; picked?: boolean; main_name?: string | null }

/** Whose website order it is (Stephanie 2026-10-16: "I still don't see a place
 *  to assign it to a rep"). A customer already in Flow goes to their account's
 *  rep automatically; a new one needs a rep picked here, and it's required
 *  before converting. Saves right away; emails no one. When the full website
 *  order flow is on, its own Assign rep box shows instead. */
function WebsiteOrderRepBox({ orderId, state, account, matched, newAccount, canMakeNewAccount, canNameNewAccount, suggestedName, savedName, current, onSaved, onAccountChange }: {
  orderId: string;
  state: RepBoxState;
  account: Account | null;
  matched: Account | null;
  newAccount: boolean;
  canMakeNewAccount: boolean;
  /** The new account's name can be typed (its column exists). */
  canNameNewAccount: boolean;
  /** What the new account is called if nobody types a name. */
  suggestedName: string;
  savedName: string | null;
  current: string | null;
  onSaved: (repId: string | null) => void;
  onAccountChange: (isNew: boolean, name: string | null) => void;
}) {
  const [name, setName] = useState(savedName ?? suggestedName);
  useEffect(() => setName(savedName ?? suggestedName), [savedName, suggestedName]);
  const [switching, setSwitching] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const setNewAccount = async (isNew: boolean, typed?: string) => {
    const nm = typed?.trim();
    if (isNew && canNameNewAccount && nm === "") { setMsg({ ok: false, text: "Type a name for the new account." }); return; }
    setSwitching(true); setMsg(null);
    try {
      const body = isNew && canNameNewAccount ? { newAccount: true, name: nm } : { newAccount: isNew };
      await api(`/website-orders/${orderId}/account`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      setConfirmNew(false);
      onAccountChange(isNew, isNew && canNameNewAccount ? nm ?? null : null);
      if (isNew && typed !== undefined && !confirmNew) setMsg({ ok: true, text: "Name saved." });
    } catch (e) {
      setMsg({ ok: false, text: apiErrorMessage(e, "Couldn't change the account.") });
    } finally { setSwitching(false); }
  };
  const [reps, setReps] = useState<Array<{ id: string; name: string }> | null>(null);
  const [saving, setSaving] = useState(false);
  // A customer already in Flow always goes to their account's rep — no picking.
  const accountRep = account?.rep_id ? account : null;
  useEffect(() => {
    if (state !== "on" || accountRep) return;
    api<{ reps: Array<{ id: string; name: string }> }>("/order-reps").then((r) => setReps(r.reps)).catch(() => setReps([]));
  }, [state, accountRep]);
  const save = async (value: string) => {
    const repId = value || null;
    setSaving(true); setMsg(null);
    try {
      await api(`/website-orders/${orderId}/rep`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ repId }) });
      onSaved(repId);
      setMsg({ ok: true, text: repId ? "Saved — this order is theirs." : "Rep removed." });
    } catch (e) {
      setMsg({ ok: false, text: apiErrorMessage(e, "Couldn't save the rep.") });
    } finally { setSaving(false); }
  };
  return (
    <section className={cn("rounded-lg border bg-canvas p-4", state === "on" && !accountRep && !current ? "border-warn" : "border-rule")}>
      <label htmlFor="website-order-rep" className="text-[15px] font-bold">Rep</label>
      {accountRep ? (
        <>
          <p className="mt-1 font-semibold">{accountRep.rep_name ?? "Their account rep"}</p>
          <p className="text-[13px] text-muted">
            {accountRep.picked
              ? `${accountRep.client_name} is the location picked above, so this goes to its rep automatically.`
              : `${accountRep.client_name} is already in Flow (same email), so this goes to their account rep automatically.`}
          </p>
          {canMakeNewAccount && !accountRep.picked ? (
            confirmNew ? (
              <div role="alert" className="mt-3 flex flex-col gap-2 rounded-lg border border-warn bg-white p-3">
                <p className="text-[13px] font-semibold">Not {accountRep.client_name}? This order will get a brand-new account instead, and you'll pick its rep.</p>
                {canNameNewAccount ? (
                  <label className="block text-[13px]">
                    <span className="font-semibold">New account name</span>
                    <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} className="mt-1" placeholder="e.g. Bozzuto – The Elms" />
                  </label>
                ) : null}
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => void setNewAccount(true, canNameNewAccount ? name : undefined)} disabled={switching}>Yes, make a new account</Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmNew(false)} disabled={switching}>Keep {accountRep.client_name}</Button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmNew(true)} className={cn("mt-2 text-[13px] font-semibold underline underline-offset-2 hover:text-ink", focusRing)}>
                Wrong account? Make a new one instead
              </button>
            )
          ) : null}
          {msg && !msg.ok ? <p role="alert" className="mt-1 text-[13px] font-semibold text-risk">{msg.text}</p> : null}
        </>
      ) : state === "needs_setup" ? (
        <p className="mt-1 text-[13px] text-muted">
          {account ? `${account.client_name} is in Flow but has no account rep. ` : "New customer. "}
          Picking a rep turns on after a quick database step from Jake (it's on his list).
        </p>
      ) : (
        <>
          <div className="mt-2 flex items-center gap-3">
            <select
              id="website-order-rep"
              value={current ?? ""}
              disabled={saving || reps === null}
              onChange={(e) => void save(e.target.value)}
              className={cn("h-10 min-w-[240px] rounded-md border border-rule bg-white px-3 text-[15px] text-ink", focusRing)}
            >
              <option value="">{reps === null ? "Loading reps…" : "Pick a rep (required)"}</option>
              {(reps ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {saving ? <Loader2 className="h-4 w-4 animate-spin text-muted" aria-hidden /> : null}
          </div>
          <p className="mt-1 text-[13px] text-muted">
            {newAccount && matched
              ? "A new account will be made for this order — pick who it goes to."
              : account ? `${account.client_name} is in Flow but has no account rep — pick who this goes to.` : "New customer — pick who this goes to."}{" "}
            Required before it's converted. Nobody is emailed.
          </p>
          {newAccount && canNameNewAccount ? (
            <form
              className="mt-3 flex flex-wrap items-end gap-2"
              onSubmit={(e) => { e.preventDefault(); void setNewAccount(true, name); }}
            >
              <label className="block text-[13px]">
                <span className="font-semibold">New account name</span>
                <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} className="mt-1 min-w-[260px]" />
              </label>
              <Button type="submit" size="sm" variant="outline" disabled={switching || name.trim() === "" || name.trim() === (savedName ?? suggestedName)}>Save name</Button>
            </form>
          ) : null}
          {newAccount && matched ? (
            <p className="mt-2 text-[13px]">
              Not linked to {matched.client_name}.{" "}
              <button type="button" onClick={() => void setNewAccount(false)} disabled={switching} className={cn("font-semibold underline underline-offset-2 hover:text-ink", focusRing)}>
                Use {matched.client_name} after all
              </button>
            </p>
          ) : null}
          {msg ? <p role={msg.ok ? "status" : "alert"} className={cn("mt-1 text-[13px] font-semibold", msg.ok ? "text-ok" : "text-risk")}>{msg.text}</p> : null}
        </>
      )}
    </section>
  );
}

/** Lines that need a logo but have none — the customer is asked for it on
 *  the thank-you page; until it arrives, someone has to chase it. */
function ArtworkMissingBadge({ count, className }: { count: number; className?: string }) {
  return (
    <span className={cn("ml-1.5 inline-block whitespace-nowrap rounded bg-risk-bg px-1.5 font-semibold text-risk", className)}>
      Artwork missing{count > 1 ? ` (${count})` : ""}
    </span>
  );
}

function OrderDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [orderRef, setOrderRef] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const panel = useModal(onClose);
  const [convertEnabled, setConvertEnabled] = useState(false);
  const [repBox, setRepBox] = useState<RepBoxState>("needs_setup");
  const [canMakeNewAccount, setCanMakeNewAccount] = useState(false);
  const [canNameNewAccount, setCanNameNewAccount] = useState(false);
  const [canPickLocation, setCanPickLocation] = useState(false);
  useEffect(() => {
    api<{ convertEnabled: boolean; repBox?: RepBoxState; canMakeNewAccount?: boolean; canNameNewAccount?: boolean; canPickLocation?: boolean }>("/website-orders-settings")
      .then((r) => { setConvertEnabled(r.convertEnabled); setRepBox(r.repBox ?? "needs_setup"); setCanMakeNewAccount(r.canMakeNewAccount ?? false); setCanNameNewAccount(r.canNameNewAccount ?? false); setCanPickLocation(r.canPickLocation ?? false); })
      .catch(() => setConvertEnabled(false));
  }, []);
  // Someone new at a company already in Flow (Bozzuto): a location must be
  // picked (or "a separate new account" chosen) before it's converted.
  const showLocation = repBox !== "flow" && d !== null && (d.status === "paid" || d.status === "open") && (d.location_choices?.length ?? 0) > 0;
  const needsLocation = showLocation && canPickLocation && !d?.picked_client_id && d?.new_account !== true;
  const pickLocation = async (choice: LocationChoice) => {
    try {
      await api(`/website-orders/${id}/location`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(choice) });
    } catch (e) {
      throw new Error(apiErrorMessage(e, "Couldn't save the location."));
    }
    setAttempt((n) => n + 1);
  };
  const separateAccount = async () => {
    try {
      await api(`/website-orders/${id}/account`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ newAccount: true }) });
      setAttempt((n) => n + 1);
    } catch (e) { setError(apiErrorMessage(e, "Couldn't change the account.")); }
  };
  // A rep is required before converting (Stephanie 2026-10-16): the account's
  // rep for a customer already in Flow, else the one picked here.
  const needsRep = repBox === "on" && d !== null && !d.existing_account?.rep_id && !d.assigned_rep_id;
  const createOrder = async () => {
    setBusy(true); setError(null);
    try {
      await api(`/website-orders/${id}/convert`, { method: "POST" });
      onChanged();
    } catch (e) { setError(apiErrorMessage(e, "Couldn't create the order.")); } finally { setBusy(false); }
  };

  useEffect(() => {
    let live = true;
    setError(null);
    api<{ order: Detail }>(`/website-orders/${id}`)
      .then((r) => { if (live) setD(r.order); })
      .catch((e) => { if (live) setError(apiErrorMessage(e, "Couldn't load this order.")); });
    return () => { live = false; };
  }, [id, attempt]);

  const setStatus = async (status: "converted" | "cancelled") => {
    setBusy(true); setError(null);
    try {
      await api(`/website-orders/${id}/status`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status, orderRef: orderRef.trim() || undefined }) });
      onChanged();
    } catch (e) { setError(apiErrorMessage(e, "Couldn't update this order.")); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/30" onClick={onClose}>
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="website-order-title"
        className="h-full w-full max-w-[640px] overflow-y-auto overscroll-contain bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <h2 id="website-order-title" className="text-[22px] font-bold">{d ? d.contact.company || d.contact.name : "Website order"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className={cn("rounded p-1 hover:bg-canvas", focusRing)}><X className="h-5 w-5" aria-hidden /></button>
        </div>
        {error ? (
          <div role="alert" className="mt-3 flex items-center gap-4">
            <p className="text-[16px] text-risk">{error}</p>
            {!d ? <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>Try again</Button> : null}
          </div>
        ) : null}
        {!d && !error ? <p className="mt-3 flex items-center gap-2 text-[16px] text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p> : null}
        {d ? (
          <div className="mt-4 flex flex-col gap-6 text-[16px]">
            {d.reference ? <p className="text-[13px] text-muted">Order reference <span className="font-mono font-semibold text-ink">{d.reference}</span> — the customer sees this on their thank-you page.</p> : null}
            {showLocation ? (
              <LocationPicker
                key={d.picked_client_id ?? "none"}
                companies={d.location_choices ?? []}
                pickedId={d.new_account ? null : d.picked_client_id ?? null}
                waitingOnJake={!canPickLocation}
                onPick={pickLocation}
                onSeparate={canMakeNewAccount && !d.new_account ? () => void separateAccount() : undefined}
              />
            ) : null}
            {repBox !== "flow" && (d.status === "paid" || d.status === "open") && !needsLocation ? (
              <WebsiteOrderRepBox
                orderId={id}
                state={repBox}
                account={d.existing_account ?? null}
                matched={d.matched_account ?? null}
                newAccount={d.new_account === true}
                canMakeNewAccount={canMakeNewAccount}
                canNameNewAccount={canNameNewAccount}
                suggestedName={d.contact.company?.trim() || d.contact.name}
                savedName={d.new_account_name ?? null}
                current={d.assigned_rep_id ?? null}
                onSaved={(repId) => setD({ ...d, assigned_rep_id: repId })}
                onAccountChange={(isNew, name) => setD({ ...d, new_account: isNew, new_account_name: isNew ? name : null, existing_account: isNew ? null : d.matched_account ?? null })}
              />
            ) : null}
            {d.artwork_missing_items?.length ? (
              <p role="status" className="rounded-lg border border-risk bg-risk-bg p-3 text-[13px] font-semibold text-risk">
                Artwork missing for {d.artwork_missing_items.map((i) => `item ${i + 1}`).join(", ")}. The customer was asked to upload it after paying — chase it before ordering.
              </p>
            ) : null}
            <section>
              <h3 className="text-[15px] font-bold">Customer{d.contact.buyerType === "individual" ? " (buying for themselves)" : ""}</h3>
              <p>{d.contact.name}{d.contact.company ? ` · ${d.contact.company}` : ""}{d.contact.department ? ` · ${d.contact.department}` : ""}</p>
              <p className="text-muted">{d.contact.email}{d.contact.phone ? ` · ${d.contact.phone}` : ""}</p>
            </section>
            <section>
              <h3 className="text-[15px] font-bold">Ships to ({d.ship_to.residential ? "home" : "business"})</h3>
              {d.ship_to.recipient ? <p>Attn: {d.ship_to.recipient}</p> : null}
              <p>{d.ship_to.street1}{d.ship_to.street2 ? `, ${d.ship_to.street2}` : ""}</p>
              <p>{d.ship_to.city}, {d.ship_to.stateCode} {d.ship_to.postalCode}</p>
            </section>
            <section>
              <h3 className="text-[15px] font-bold">Billing address</h3>
              {d.contact.billing ? (
                <>
                  {d.contact.billing.name ? <p>{d.contact.billing.name}</p> : null}
                  <p>{d.contact.billing.street1}{d.contact.billing.street2 ? `, ${d.contact.billing.street2}` : ""}</p>
                  <p>{d.contact.billing.city}, {d.contact.billing.stateCode} {d.contact.billing.postalCode}{d.contact.billing.country ? ` · ${d.contact.billing.country}` : ""}</p>
                </>
              ) : <p className="text-muted">Same as shipping</p>}
            </section>
            <section className="flex flex-col gap-3">
              <h3 className="text-[15px] font-bold">Items</h3>
              {d.priced.items.map((it, i) => {
                // Paper lines carry no colour quantities (their options are in `lines`).
                const sizes = d.items[i]?.colorQuantities
                  ?.map((c) => ({ color: c.color, sizes: Object.entries(c.quantities).filter(([, n]) => n > 0).map(([s, n]) => `${s} ${n}`).join(", ") }))
                  .filter((c) => c.sizes) ?? [];
                return (
                  <div key={i} className="rounded-lg border border-rule bg-white p-4">
                    <p className="font-semibold">{it.name} — {it.quantity} pcs{d.artwork_missing_items?.includes(i) ? <ArtworkMissingBadge count={1} className="text-[13px]" /> : null}</p>
                    {sizes.length ? <p className="text-[13px] text-muted">{sizes.map((c) => `${c.color}: ${c.sizes}`).join(" · ")}</p> : null}
                    <ul className="mt-2 text-[13px]">
                      {it.lines.map((l) => <li key={l.label} className="flex justify-between"><span>{l.label}</span><span className="tabular-nums">{money(l.amount)}</span></li>)}
                    </ul>
                    {it.logosNeedReview ? <p className="mt-2 text-[13px] font-semibold text-warn">Check this logo's colors before ordering.</p> : null}
                  </div>
                );
              })}
            </section>
            <section>
              <h3 className="text-[15px] font-bold">{d.total_paid ? "Charged" : "Quoted (not paid)"}</h3>
              <dl className="grid grid-cols-[1fr_auto] gap-y-1 tabular-nums">
                <dt>Items</dt><dd className="text-right">{money(d.subtotal)}</dd>
                <dt>Shipping{d.priced.transitDays ? ` (ground, ~${d.priced.transitDays} days)` : ""}</dt><dd className="text-right">{money(d.shipping)}</dd>
                <dt>Tax</dt><dd className="text-right">{d.tax === null ? "Not charged yet" : money(d.tax)}</dd>
                <dt className="font-semibold">Total charged</dt><dd className="text-right font-semibold">{d.total_paid === null ? "Not paid" : money(d.total_paid)}</dd>
              </dl>
            </section>
            <section className="flex flex-col gap-2">
              <h3 className="text-[15px] font-bold">Logos</h3>
              {d.logos.length === 0 ? <p className="text-muted">No logo uploaded — the customer will send art.</p> : null}
              {d.logos.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-3 rounded-lg border border-rule bg-white p-4">
                  <div>
                    <p className="font-semibold">{l.name}</p>
                    <p className="flex flex-wrap items-center gap-1 text-[13px] text-muted">
                      {l.analysis?.inkColours ? `${l.analysis.inkColours} color${l.analysis.inkColours === 1 ? "" : "s"}` : "Colors not read — check the art"}
                      {l.analysis?.fullColour ? " · full color" : ""}
                      {(l.analysis?.palette ?? []).map((c) => (
                        <span key={c} className="inline-flex items-center gap-1">
                          <span className="inline-block h-3 w-3 rounded-sm border border-rule" style={{ background: c }} aria-hidden />
                          <span className="font-mono">{c}</span>
                          {nearestPms(c) && <span className="text-ink">≈ {nearestPms(c)}</span>}
                        </span>
                      ))}
                    </p>
                    {(l.analysis?.palette ?? []).length > 0 && (
                      <p className="mt-1 text-[12px] text-muted">Closest PMS, read from the image — confirm with the art before printing.</p>
                    )}
                  </div>
                  {l.url ? <a href={l.url} target="_blank" rel="noreferrer" className={cn("flex items-center gap-1 rounded text-[15px] font-semibold underline-offset-2 hover:underline", focusRing)}><Download className="h-4 w-4" aria-hidden /> Download</a> : null}
                </div>
              ))}
            </section>
            {d.notes ? <section><h3 className="text-[15px] font-bold">Notes</h3><p className="whitespace-pre-wrap">{d.notes}</p></section> : null}
            {d.status === "paid" || d.status === "converted" ? <WebsiteAccountPanel kind="order" id={id} /> : null}
            {d.status === "paid" ? (
              <section className="flex flex-col gap-2 border-t border-rule pt-4">
                <h3 className="text-[15px] font-bold">Create it in the workflow</h3>
                <p className="text-[13px] text-muted">
                  {convertEnabled
                    ? "Creates a quote for the rep: every item, size, decoration and logo, the ship-to and billing addresses, shipping and the tax Stripe collected — with the card payment recorded on it. The rep reviews it and converts it to the invoice. Add net costs on the order."
                    : "One-click order creation turns on at launch. Until then, create the order by hand and mark it converted below."}
                </p>
                <div>
                  <Button onClick={() => void createOrder()} disabled={busy || !convertEnabled || needsRep || needsLocation} title={needsLocation ? "Pick the location first." : needsRep ? "Pick a rep first." : undefined}>Create order</Button>
                </div>
              </section>
            ) : null}
            {d.status === "paid" ? (
              <section className="flex flex-col gap-3 border-t border-rule pt-4">
                {/* Required (Stephanie 2026-10-15) — every converted website order
                    traces to its Flow order. "Create order" above numbers it for
                    you once it's switched on. */}
                <label htmlFor="website-order-ref" className="text-[15px] font-bold">Flow order number</label>
                <Input id="website-order-ref" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} maxLength={80} placeholder="e.g. 70012" />
                <p className="text-[13px] text-muted">Needed to mark it converted — the order you made in Flow for this.</p>
                {needsLocation ? <p className="text-[13px] font-semibold text-warn">Pick the location at the top first.</p> : needsRep ? <p className="text-[13px] font-semibold text-warn">Pick a rep at the top first — it can't be converted without one.</p> : null}
                {confirmCancel ? (
                  <div role="alert" className="flex flex-col gap-3 rounded-lg border border-risk p-4">
                    <p className="font-semibold">Cancel this order? Refund the card in Stripe first — cancelling here does not refund it.</p>
                    <div className="flex gap-3">
                      <Button variant="destructive" onClick={() => void setStatus("cancelled")} disabled={busy}>Yes, cancel it</Button>
                      <Button variant="outline" onClick={() => setConfirmCancel(false)} disabled={busy}>Keep it</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <Button onClick={() => void setStatus("converted")} disabled={busy || orderRef.trim() === "" || needsRep} title={needsRep ? "Pick a rep first." : orderRef.trim() === "" ? "Add the Flow order number first." : undefined}><Check className="mr-1 h-4 w-4" aria-hidden /> Mark converted</Button>
                    <Button variant="outline" onClick={() => setConfirmCancel(true)} disabled={busy}>Cancel order…</Button>
                  </div>
                )}
              </section>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Requests ─────────────────────────────────────────────────────────────────
type RequestStatus = "new" | "handled" | "cancelled";
interface RequestRow { id: string; status: RequestStatus; kind: "cart" | "inquiry"; contact: Contact; ship_to: Partial<ShipTo> | null; created_at: string; handled_at: string | null; item_count: number; message: string | null }
interface RequestItem { name: string; quantity?: number | null; colorQuantities?: Array<{ color: string; quantities: Record<string, number> }>; method?: string | null; placements?: string[]; options?: Record<string, string>; notes?: string | null }
interface RequestDetailRow extends RequestRow {
  items: RequestItem[];
  details: Record<string, string>;
  notes: string | null;
  logos: Detail["logos"];
}
const REQUEST_WORD: Record<RequestStatus, [string, string]> = {
  new: ["New", "bg-warn-bg text-warn"],
  handled: ["Handled", "bg-ok-bg text-ok"],
  cancelled: ["Closed", "bg-canvas text-muted"],
};
const DETAIL_LABEL: Record<string, string> = { budget: "Budget", deadline: "Needed by", payment: "Payment", po: "PO / reference", page: "Sent from" };

function RequestsList({ onChanged }: { onChanged: () => void }) {
  const [filter, setFilter] = useState<"new" | "all">("new");
  const [rows, setRows] = useState<RequestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError(null);
    setRows(null);
    try {
      const r = await api<{ requests: RequestRow[] }>(`/website-requests?status=${filter}`);
      setRows(r.requests);
    } catch (e) { setError(apiErrorMessage(e, "Couldn't load website requests.")); }
  }, [filter]);
  useEffect(() => { void load(); }, [load]);
  const close = useCallback(() => setOpenId(null), []);

  return (
    <div className="mt-6">
      <div className="flex gap-5" role="group" aria-label="Which requests">
        {([["new", "New"], ["all", "All requests"]] as const).map(([k, label]) => (
          <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}
            className={cn("border-b pb-1 text-[13px] font-semibold", focusRing, filter === k ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink")}>
            {label}
          </button>
        ))}
      </div>
      {error ? (
        <div role="alert" className="mt-4 flex items-center gap-4">
          <p className="text-[16px] text-risk">{error}</p>
          <Button variant="outline" onClick={() => void load()}>Try again</Button>
        </div>
      ) : null}
      {rows === null && !error ? <p className="mt-6 flex items-center gap-2 text-[16px] text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p> : null}
      {rows && rows.length === 0 ? <p className="mt-6 text-[16px] text-muted">{filter === "new" ? "No new requests." : "No website requests yet."}</p> : null}
      {rows && rows.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-lg border border-rule">
          <table className="w-full min-w-[760px] text-left text-[13px] tabular-nums">
            <caption className="sr-only">Website requests: customer, what they sent, date and status</caption>
            <thead className="bg-canvas text-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Customer</th>
                <th scope="col" className="px-4 py-3 font-semibold">Request</th>
                <th scope="col" className="px-4 py-3 font-semibold">When</th>
                <th scope="col" className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-rule">
                  <th scope="row" className="px-4 py-3 text-left font-normal">
                    <button type="button" className={cn("rounded text-left font-semibold underline-offset-2 hover:underline", focusRing)} onClick={() => setOpenId(r.id)}>
                      {r.contact.company || r.contact.name}
                    </button>
                    <div className="text-muted">{r.contact.email}</div>
                  </th>
                  <td className="max-w-[44ch] px-4 py-3">
                    <span className="font-semibold">{r.kind === "cart" ? "Quote request" : "Message"}</span>
                    {r.kind === "cart"
                      ? <span className="text-muted"> · {r.item_count} item{r.item_count === 1 ? "" : "s"}</span>
                      : <span className="line-clamp-2 text-muted">{r.message}</span>}
                  </td>
                  <td className="px-4 py-3">{when(r.created_at)}</td>
                  <td className="px-4 py-3"><span className={cn("inline-block whitespace-nowrap rounded px-1.5 font-semibold", REQUEST_WORD[r.status][1])}>{REQUEST_WORD[r.status][0]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {openId ? <RequestDetail id={openId} onClose={close} onChanged={() => { setOpenId(null); void load(); onChanged(); }} /> : null}
    </div>
  );
}

function RequestDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<RequestDetailRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [orderRef, setOrderRef] = useState("");
  const [attempt, setAttempt] = useState(0);
  const panel = useModal(onClose);

  useEffect(() => {
    let live = true;
    setError(null);
    api<{ request: RequestDetailRow }>(`/website-requests/${id}`)
      .then((r) => { if (live) setD(r.request); })
      .catch((e) => { if (live) setError(apiErrorMessage(e, "Couldn't load this request.")); });
    return () => { live = false; };
  }, [id, attempt]);

  const setStatus = async (status: RequestStatus) => {
    setBusy(true); setError(null);
    try {
      await api(`/website-requests/${id}/status`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status, orderRef: orderRef.trim() || undefined }) });
      onChanged();
    } catch (e) { setError(apiErrorMessage(e, "Couldn't update this request.")); } finally { setBusy(false); }
  };
  const ship = d?.ship_to;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/30" onClick={onClose}>
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="website-request-title"
        className="h-full w-full max-w-[640px] overflow-y-auto overscroll-contain bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <h2 id="website-request-title" className="text-[22px] font-bold">{d ? d.contact.company || d.contact.name : "Website request"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className={cn("rounded p-1 hover:bg-canvas", focusRing)}><X className="h-5 w-5" aria-hidden /></button>
        </div>
        {error ? (
          <div role="alert" className="mt-3 flex items-center gap-4">
            <p className="text-[16px] text-risk">{error}</p>
            {!d ? <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>Try again</Button> : null}
          </div>
        ) : null}
        {!d && !error ? <p className="mt-3 flex items-center gap-2 text-[16px] text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p> : null}
        {d ? (
          <div className="mt-4 flex flex-col gap-6 text-[16px]">
            {d.status === "new"
              ? <p className="text-[13px] text-muted">Not paid — the customer is waiting for a price or a reply.</p>
              : <p className="text-[13px] text-muted">{d.status === "handled" ? "Handled" : "Closed"} {when(d.handled_at)}.</p>}
            <section>
              <h3 className="text-[15px] font-bold">Customer</h3>
              <p>{d.contact.name}{d.contact.company ? ` · ${d.contact.company}` : ""}</p>
              <p className="text-muted">
                <a href={`mailto:${d.contact.email}`} className={cn("rounded underline-offset-2 hover:underline", focusRing)}>{d.contact.email}</a>
                {d.contact.phone ? ` · ${d.contact.phone}` : ""}
              </p>
            </section>
            {ship && (ship.street1 || ship.city) ? (
              <section>
                <h3 className="text-[15px] font-bold">Ships to{ship.residential === undefined ? "" : ship.residential ? " (home)" : " (business)"}</h3>
                {ship.street1 ? <p>{ship.street1}{ship.street2 ? `, ${ship.street2}` : ""}</p> : null}
                <p>{[ship.city, ship.stateCode].filter(Boolean).join(", ")} {ship.postalCode ?? ""}</p>
              </section>
            ) : null}
            {d.message ? <section><h3 className="text-[15px] font-bold">Message</h3><p className="whitespace-pre-wrap">{d.message}</p></section> : null}
            {Object.keys(d.details ?? {}).length ? (
              <section>
                <h3 className="text-[15px] font-bold">Details</h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                  {Object.entries(d.details).map(([k, v]) => <div key={k} className="contents"><dt className="text-muted">{DETAIL_LABEL[k] ?? k.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase())}</dt><dd>{v}</dd></div>)}
                </dl>
              </section>
            ) : null}
            {d.items.length ? (
              <section className="flex flex-col gap-3">
                <h3 className="text-[15px] font-bold">Items</h3>
                {d.items.map((it, i) => {
                  const sizes = (it.colorQuantities ?? [])
                    .map((c) => ({ color: c.color, sizes: Object.entries(c.quantities).filter(([, n]) => n > 0).map(([s, n]) => `${s} ${n}`).join(", ") }))
                    .filter((c) => c.sizes);
                  return (
                    <div key={i} className="rounded-lg border border-rule bg-white p-4">
                      <p className="font-semibold">{it.name}{it.quantity ? ` — ${it.quantity} pcs` : ""}</p>
                      {sizes.length ? <p className="text-[13px] text-muted">{sizes.map((c) => `${c.color}: ${c.sizes}`).join(" · ")}</p> : null}
                      {it.method ? <p className="text-[13px]">{it.method}{it.placements?.length ? ` · ${it.placements.join(", ")}` : ""}</p> : null}
                      {it.options && Object.keys(it.options).length ? <p className="text-[13px] text-muted">{Object.entries(it.options).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p> : null}
                      {it.notes ? <p className="mt-1 whitespace-pre-wrap text-[13px]">{it.notes}</p> : null}
                    </div>
                  );
                })}
              </section>
            ) : null}
            {d.logos.length ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-[15px] font-bold">Logos</h3>
                {d.logos.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-3 rounded-lg border border-rule bg-white p-4">
                    <p className="font-semibold">{l.name}</p>
                    {l.url ? <a href={l.url} target="_blank" rel="noreferrer" className={cn("flex items-center gap-1 rounded text-[15px] font-semibold underline-offset-2 hover:underline", focusRing)}><Download className="h-4 w-4" aria-hidden /> Download</a> : null}
                  </div>
                ))}
              </section>
            ) : null}
            {d.notes ? <section><h3 className="text-[15px] font-bold">Notes</h3><p className="whitespace-pre-wrap">{d.notes}</p></section> : null}
            <WebsiteAccountPanel kind="request" id={id} />
            <section className="flex flex-col gap-3 border-t border-rule pt-4">
              {d.status === "new" ? (
                <>
                  <label htmlFor="website-request-ref" className="text-[15px] font-bold">Flow order or quote number (optional)</label>
                  <Input id="website-request-ref" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} maxLength={80} placeholder="e.g. 70012" />
                  <div className="flex gap-3">
                    <Button onClick={() => void setStatus("handled")} disabled={busy}><Check className="mr-1 h-4 w-4" aria-hidden /> Mark handled</Button>
                    <Button variant="outline" onClick={() => void setStatus("cancelled")} disabled={busy}>Close without action</Button>
                  </div>
                </>
              ) : (
                <div><Button variant="outline" onClick={() => void setStatus("new")} disabled={busy}>Move back to new</Button></div>
              )}
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
